import type { ExternalBooking } from 'patient-api-lib'
import { useCallback, useEffect, useState } from 'react'
import Session from 'supertokens-auth-react/recipe/session'

import { useDebounceEffect } from '@/hooks/useDebounceEffect'
import { useAppStore } from '@/store/app'

import { getMyNextVisit } from '../helpers'

export const useNextBooking = () => {
  const [nextBooking, setNextBooking] = useState<ExternalBooking.AsObject | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const { clinicIds } = useAppStore((state) => state)

  const fetchNextVisit = useCallback(async () => {
    try {
      const session = await Session.doesSessionExist()

      if (!session) {
        throw new Error('Session does not exist')
      }

      const accessToken = await Session.getAccessToken()
      const response = await getMyNextVisit(accessToken, clinicIds)
      setNextBooking(response)
      setError(false)
    } catch (err) {
      setError(true)
      setNextBooking(null)
      if (err instanceof Error) {
        throw new Error(`Error fetching next visit: ${err.message}`)
      }
      throw new Error('Error fetching next visit')
    } finally {
      setLoading(false)
    }
  }, [clinicIds])

  useEffect(() => {
    fetchNextVisit()
  }, [fetchNextVisit])

  useDebounceEffect(() => {
    fetchNextVisit()
  }, 1000 * 60)

  return {
    nextBooking,
    loading,
    error,
    refetch: fetchNextVisit,
  }
}
